import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { expensesService, Expense } from '../services/expenses.service';
import { expenseTagsService, ExpenseTag, ExpenseTagCreate } from '../services/expenseTags.service';
import { propertiesService, Property } from '../services/properties.service';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import { FiPlus, FiEdit2, FiTrash2, FiCalendar, FiDollarSign, FiPaperclip, FiEdit, FiX, FiTag } from 'react-icons/fi';
import { tw, monoFont } from '../utils/terminalTheme';

export const PropertyExpenses: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [property, setProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalExpenses: 0,
    ytdExpenses: 0,
    avgExpense: 0,
    expenseCount: 0
  });

  const [tags, setTags] = useState<ExpenseTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [tagStats, setTagStats] = useState<{ [key: number]: { count: number; total: number } }>({});
  const [editingTagsForExpense, setEditingTagsForExpense] = useState<number | null>(null);

  useEffect(() => {
    if (propertyId) {
      loadData();
    }
  }, [propertyId, selectedTags]);

  const loadData = async () => {
    try {
      const [expensesData, propertiesData, tagsData] = await Promise.all([
        expensesService.getExpenses(parseInt(propertyId!), selectedTags.length > 0 ? selectedTags : undefined),
        propertiesService.getProperties(),
        expenseTagsService.getTags()
      ]);

      const currentProperty = propertiesData.find(p => p.id === parseInt(propertyId!));
      setProperty(currentProperty || null);
      setExpenses(expensesData);
      setTags(tagsData);

      const currentYear = new Date().getFullYear();
      const ytdExpenses = expensesData.filter(expense => {
        const expenseYear = new Date(expense.expense_date).getFullYear();
        return expenseYear === currentYear;
      });

      const totalExpenses = expensesData.reduce((sum, e) => sum + e.amount, 0);
      const ytdTotal = ytdExpenses.reduce((sum, e) => sum + e.amount, 0);
      const avgExpense = expensesData.length > 0 ? totalExpenses / expensesData.length : 0;

      setStats({
        totalExpenses,
        ytdExpenses: ytdTotal,
        avgExpense,
        expenseCount: expensesData.length
      });

      if (selectedTags.length > 0) {
        const statsResponse = await expenseTagsService.getTagStats(selectedTags);
        const statsMap: { [key: number]: { count: number; total: number } } = {};
        statsResponse.tags.forEach(tag => {
          statsMap[tag.id] = { count: tag.expense_count, total: tag.total_amount };
        });
        setTagStats(statsMap);
      }
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await expensesService.deleteExpense(id);
        toast.success('Expense deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete expense');
      }
    }
  };

  const handleViewDocument = (documentId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const token = localStorage.getItem('accessToken');
    const baseUrl = api.defaults.baseURL || '';
    window.open(`${baseUrl}/documents/${documentId}/preview?token=${token}`, '_blank');
  };

  const cleanDescription = (desc: string | undefined): string => {
    if (!desc) return 'N/A';
    return desc
      .replace(/\s*\[Auto-extracted from email(?:\s*-\s*backfill)?\]\s*/gi, '')
      .replace(/\s*\[backfill\]\s*/gi, '')
      .trim() || 'N/A';
  };

  const handleTagClick = (tagId: number) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const color = '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
      await expenseTagsService.createTag({ name: newTagName, color });
      toast.success('Tag created successfully');
      setNewTagName('');
      loadData();
    } catch (error) {
      toast.error('Failed to create tag');
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (window.confirm('Are you sure you want to delete this tag?')) {
      try {
        await expenseTagsService.deleteTag(tagId);
        toast.success('Tag deleted successfully');
        setSelectedTags(prev => prev.filter(id => id !== tagId));
        loadData();
      } catch (error) {
        toast.error('Failed to delete tag');
      }
    }
  };

  const handleAddTagToExpense = async (expenseId: number, tagId: number) => {
    try {
      await expenseTagsService.addTagToExpense(expenseId, tagId);
      toast.success('Tag added to expense');
      loadData();
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTagFromExpense = async (expenseId: number, tagId: number) => {
    try {
      await expenseTagsService.removeTagFromExpense(expenseId, tagId);
      toast.success('Tag removed from expense');
      loadData();
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 font-mono">Property not found</p>
        <Link to="/" className="text-cyan-400 hover:text-cyan-300 font-mono text-sm mt-4 inline-block">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
              <span className="text-cyan-400">~/</span>properties/<span className="text-cyan-400">{property.name?.toLowerCase().replace(/\s+/g, '-')}</span>/expenses
            </h1>
            <p className="text-gray-500 text-sm font-mono mt-1">{property.city}, {property.state}</p>
          </div>
          <Link
            to="/expenses/new"
            className={tw.btnPrimary + ' flex items-center gap-2'}
          >
            <FiPlus className="w-4 h-4" />
            Add Expense
          </Link>
        </div>
      </div>

      {/* Tag Filter Section */}
      <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-mono text-white flex items-center uppercase tracking-wider">
            <FiTag className="mr-2 text-cyan-400" />
            Filter by Tags
          </h3>
          <button
            onClick={() => setShowTagManager(!showTagManager)}
            className={tw.btnGhost}
          >
            {showTagManager ? 'Hide' : 'Manage'} Tags
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {tags.map(tag => (
            <button
              key={tag.id}
              onClick={() => handleTagClick(tag.id)}
              className={`px-3 py-1 rounded-full text-xs font-mono font-medium transition-all ${
                selectedTags.includes(tag.id)
                  ? 'bg-opacity-100 text-white ring-2 ring-offset-2 ring-offset-gray-800'
                  : 'bg-opacity-20 hover:bg-opacity-30 text-gray-300'
              }`}
              style={{
                backgroundColor: selectedTags.includes(tag.id) ? tag.color : tag.color + '33'
              }}
            >
              {tag.name}
              {selectedTags.includes(tag.id) && tagStats[tag.id] && (
                <span className="ml-2 text-[10px]">
                  ({tagStats[tag.id].count} | {formatCurrency(tagStats[tag.id].total)})
                </span>
              )}
            </button>
          ))}

          {tags.length === 0 && (
            <p className="text-gray-600 text-xs font-mono">No tags created yet</p>
          )}
        </div>

        {selectedTags.length > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-700/50">
            <p className="text-xs text-gray-500 font-mono">
              Showing expenses with selected tags
            </p>
            <button
              onClick={() => setSelectedTags([])}
              className={tw.btnGhost}
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Tag Manager */}
        {showTagManager && (
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <h4 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Create New Tag</h4>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                placeholder="Tag name"
                className={tw.input}
              />
              <button
                onClick={handleCreateTag}
                className={tw.btnPrimary}
              >
                Create
              </button>
            </div>

            {tags.length > 0 && (
              <div>
                <h4 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Manage Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <div
                      key={tag.id}
                      className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono"
                      style={{ backgroundColor: tag.color + '33' }}
                    >
                      <span className="text-gray-300">{tag.name}</span>
                      <button
                        onClick={() => handleDeleteTag(tag.id)}
                        className="text-red-400 hover:text-red-300 ml-1"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className={tw.statCard}>
          <p className={tw.statLabel}>{selectedTags.length > 0 ? 'Filtered Total' : 'Total Expenses'}</p>
          <p className={tw.statValueCyan}>{formatCurrency(stats.totalExpenses)}</p>
          {selectedTags.length > 0 && (
            <p className="text-xs text-cyan-400/60 font-mono mt-1">{stats.expenseCount} expenses</p>
          )}
        </div>

        <div className={tw.statCard}>
          <p className={tw.statLabel}>{selectedTags.length > 0 ? 'Filtered YTD' : 'YTD Expenses'}</p>
          <p className="text-2xl font-bold font-mono text-amber-400">{formatCurrency(stats.ytdExpenses)}</p>
        </div>

        <div className={tw.statCard}>
          <p className={tw.statLabel}>Average Expense</p>
          <p className="text-2xl font-bold font-mono text-purple-400">{formatCurrency(stats.avgExpense)}</p>
        </div>

        <div className={tw.statCard}>
          <p className={tw.statLabel}>Total Count</p>
          <p className={tw.statValueCyan}>{stats.expenseCount}</p>
        </div>
      </div>

      {/* Expenses Table */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-3 border-b border-gray-700/50 flex items-center justify-between">
          <h2 className="text-sm font-mono text-gray-400 uppercase tracking-wider">Expense History</h2>
          <span className="text-xs text-gray-600 font-mono">{expenses.length} records</span>
        </div>

        {expenses.length === 0 ? (
          <div className={tw.empty}>
            <p className="mb-4">No expenses recorded yet</p>
            <Link
              to="/expenses/new"
              className={tw.btnPrimary + ' inline-flex items-center gap-2'}
            >
              <FiPlus className="w-4 h-4" />
              Add First Expense
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30">
                  <th className={tw.tableHeader}>Date</th>
                  <th className={tw.tableHeader}>Vendor</th>
                  <th className={tw.tableHeader}>Category</th>
                  <th className={tw.tableHeader}>Description</th>
                  <th className={tw.tableHeaderRight}>Amount</th>
                  <th className={tw.tableHeader}>Tags</th>
                  <th className={tw.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {expenses.map((expense) => (
                  <tr
                    key={expense.id}
                    className={tw.tableRow + ' cursor-pointer'}
                    onClick={() => toast(`Expense Details:\nVendor: ${expense.vendor || 'N/A'}\nAmount: ${formatCurrency(expense.amount)}\nDate: ${formatDate(expense.expense_date)}\nDescription: ${expense.description || 'N/A'}`)}
                  >
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">{formatDate(expense.expense_date)}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-white font-mono">{expense.vendor || 'N/A'}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-xs text-gray-400 font-mono">
                        {expense.category?.name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className={tw.tableCell}>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-400 font-mono">{cleanDescription(expense.description)}</span>
                        {expense.document_id && (
                          <button
                            onClick={(e) => handleViewDocument(expense.document_id!, e)}
                            className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
                            title="View attached document"
                          >
                            <FiPaperclip className="w-3.5 h-3.5" />
                            <span className="text-[10px] ml-1 font-mono">View</span>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={tw.tableCellRight}>
                      <span className="text-sm font-mono font-medium text-white">
                        {formatCurrency(expense.amount)}
                      </span>
                    </td>
                    <td className={tw.tableCell}>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-wrap gap-1">
                          {expense.tags?.map(tag => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-medium text-white"
                              style={{ backgroundColor: tag.color }}
                            >
                              {tag.name}
                              {editingTagsForExpense === expense.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTagFromExpense(expense.id, tag.id);
                                  }}
                                  className="ml-1 hover:text-gray-200"
                                >
                                  <FiX className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                        {editingTagsForExpense === expense.id ? (
                          <div className="flex items-center gap-1">
                            <select
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const tagId = parseInt(e.target.value);
                                if (tagId) {
                                  handleAddTagToExpense(expense.id, tagId);
                                  e.target.value = '';
                                }
                              }}
                              className="text-[10px] px-2 py-1 bg-gray-900 text-white rounded border border-gray-700 font-mono"
                            >
                              <option value="">Add tag...</option>
                              {tags
                                .filter(tag => !expense.tags?.some(t => t.id === tag.id))
                                .map(tag => (
                                  <option key={tag.id} value={tag.id}>
                                    {tag.name}
                                  </option>
                                ))}
                            </select>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTagsForExpense(null);
                              }}
                              className="text-gray-500 hover:text-gray-300"
                            >
                              <FiX className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTagsForExpense(expense.id);
                            }}
                            className="text-gray-600 hover:text-cyan-400 transition-colors"
                            title="Manage tags"
                          >
                            <FiTag className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={tw.tableCell + ' whitespace-nowrap'}>
                      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toast('Edit functionality coming soon');
                          }}
                          className="text-gray-500 hover:text-cyan-400 transition-colors"
                          title="Edit expense"
                        >
                          <FiEdit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(expense.id);
                          }}
                          className="text-gray-500 hover:text-red-400 transition-colors"
                          title="Delete expense"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
